import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  ScaleIcon,
  ChartBarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import ToastContainer, { showToast } from '../../components/common/Toast';
import ConfirmModal from '../../components/common/ConfirmModal';
import KeyDisputeCards from '../../components/disputes/cards/KeyDisputeCards';

// Import Dispute Components
import DisputesTable from '../../components/disputes/DisputesTable';
import DisputeFormModal from '../../components/disputes/DisputeFormModal';
import DisputeDetailsModal from '../../components/disputes/DisputeDetailsModal';
import DisputeDashboard from '../../components/disputes/cards/DisputeDashboard';
/* import ScheduleHearingModal from '../components/disputes/ScheduleHearingModal';
import DisputeNotesModal from '../components/disputes/DisputeNotesModal'; */

// Import API services
import { disputesAPI, residentsAPI } from '../../services/api';

function DisputesPage() {
  // State Management
  const [disputes, setDisputes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [statistics, setStatistics] = useState({
    active_cases: 0,
    pending_cases: 0,
    settled_cases: 0,
    escalated_cases: 0,
    scheduled_hearings: 0,
    case_types: {
      settlement: 0,
      arbitration: 0,
      mediation: 0,
    },
    avg_resolution_days: 0,
    resolution_rate: 0,
    successful_mediations: 0,
    this_month: {
      new_cases: 0,
      resolved: 0,
      growth_rate: 0,
      active_change: 0,
      pending_change: 0,
      settled_change: 0,
      escalated_change: 0,
      hearings_change: 0,
    },
  });
  const [showStatistics, setShowStatistics] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [dateFiledFilter, setDateFiledFilter] = useState(null);
  const [residencyFilter, setResidencyFilter] = useState(null);
  // Fetch disputes
  useEffect(() => {
    fetchDisputes();
  }, [page, perPage, search, statusFilter, dateRange]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await disputesAPI.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching dispute statistics:', error);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, [
    page,
    perPage,
    search,
    statusFilter,
    dateFiledFilter,
    residencyFilter,
    dateRange,
  ]);
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, dateFiledFilter, residencyFilter]);
  const fetchDisputes = async () => {
    setIsLoading(true);
    try {
      const response = await disputesAPI.getAll({
        page,
        perPage,
        search,
        status: statusFilter !== 'all' ? statusFilter : null,
        date_filed: dateFiledFilter,
        residency: residencyFilter,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      });

      console.log('Disputes API Response:', response);

      // Handle different response formats
      if (response.data && response.data.data) {
        // Laravel paginated response
        setDisputes(
          Array.isArray(response.data.data) ? response.data.data : []
        );
        setTotal(response.data.total || 0);
        setLastPage(response.data.last_page || 1);
      } else {
        // Direct data response
        setDisputes(Array.isArray(response.data) ? response.data : []);
        setTotal(
          response.meta?.total ||
            (Array.isArray(response.data) ? response.data.length : 0)
        );
        setLastPage(response.meta?.last_page || 1);
      }
    } catch (error) {
      console.error('Error fetching disputes:', error);
      showToast('Failed to load disputes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add/edit form submission
  const handleFormSubmit = async (formData) => {
    try {
      if (formData.id) {
        await disputesAPI.update(formData.id, formData);
        showToast('Dispute updated successfully', 'success');
      } else {
        await disputesAPI.create(formData);
        showToast('New dispute filed successfully', 'success');
      }
      setShowForm(false);
      fetchDisputes();
    } catch (error) {
      console.error('Error saving dispute:', error);
      showToast('Failed to save dispute', 'error');
    }
  };
  const handleDeleteClick = (dispute) => {
    setConfirmTarget(dispute);
    setConfirmOpen(true);
  };
  const confirmDelete = async () => {
    if (!confirmTarget) return;

    setConfirmLoading(true);
    try {
      await disputesAPI.delete(confirmTarget.id);
      showToast('Dispute deleted successfully', 'success');
      fetchDisputes();
    } catch (error) {
      console.error('Error deleting dispute:', error);
      showToast('Failed to delete dispute', 'error');
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  };

  // Open dispute details
  const handleViewDetails = (dispute) => {
    setSelectedDispute(dispute);
    setShowDetails(true);
  };

  // Schedule hearing
  const handleScheduleHearing = (dispute) => {
    setSelectedDispute(dispute);
    setShowSchedule(true);
  };

  // Add notes
  const handleAddNotes = (dispute) => {
    setSelectedDispute(dispute);
    setShowNotes(true);
  };

  // Handle scheduling submission
  const handleScheduleSubmit = async (scheduleData) => {
    try {
      await disputesAPI.scheduleHearing(selectedDispute.id, scheduleData);
      showToast('Hearing scheduled successfully', 'success');
      setShowSchedule(false);
      fetchDisputes();
    } catch (error) {
      console.error('Error scheduling hearing:', error);
      showToast('Failed to schedule hearing', 'error');
    }
  };

  // Handle notes submission
  const handleNotesSubmit = async (notesData) => {
    try {
      // Check if we're receiving FormData (with attachment)
      const isFormData = notesData instanceof FormData;
      const headers = isFormData
        ? { 'Content-Type': 'multipart/form-data' }
        : {};

      await disputesAPI.addNotes(selectedDispute.id, notesData);
      showToast('Notes added successfully', 'success');
      setShowNotes(false);
      fetchDisputes();
    } catch (error) {
      console.error('Error adding notes:', error);
      showToast('Failed to add notes', 'error');
    }
  };

  return (
    <div className='max-w-9xl mx-auto p-2'>
      <ToastContainer />

      {/* Header Section */}
      <div className='flex justify-between items-center mb-6'>
        <div className='flex items-center gap-3'>
          <div className='bg-blue-100 p-2 rounded-lg'>
            <ScaleIcon className='w-6 h-6 text-blue-600' />
          </div>
          <div>
            <h1 className='text-2xl font-semibold text-gray-800'>
              Barangay Justice System
            </h1>
            <p className='text-gray-600'>
              Manage disputes, blotter records, and hearings
            </p>
          </div>
        </div>

        <div>
          <button
            onClick={() => {
              setSelectedDispute(null);
              setShowForm(true);
            }}
            className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          >
            <PlusIcon className='w-4 h-4 mr-2' />
            File New Blotter
          </button>
        </div>
      </div>

      <KeyDisputeCards statistics={statistics} />

      {/* Case Analytics Dashboard */}
      <div className='bg-white rounded-xl shadow-sm mb-6'>
        <div className='flex items-center justify-between'>
          <button
            onClick={() => setShowStatistics(!showStatistics)}
            className='w-full px-6 py-4 flex items-center justify-between text-left transition-all duration-200 rounded-lg'
          >
            <div className='flex items-center'>
              <ChartBarIcon className='w-6 h-6 mr-2 text-blue-600' />
              <div>
                <h2 className='text-lg font-medium text-gray-800'>
                  Case Analytics Dashboard
                </h2>
                <p className='ml-2 text-sm text-gray-500'>
                  Comprehensive case metrics and statistics
                </p>
              </div>
            </div>

            {showStatistics ? (
              <ChevronUpIcon className='w-5 h-5' />
            ) : (
              <ChevronDownIcon className='w-5 h-5' />
            )}
          </button>
        </div>

        {showStatistics && (
          <div className='mt-4 p-4'>
            <DisputeDashboard statistics={statistics} />
          </div>
        )}
      </div>

      <div className='bg-white rounded-xl shadow-sm flex-1 overflow-hidden'>
        <DisputesTable
          disputes={disputes}
          isLoading={isLoading}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          dateFiledFilter={dateFiledFilter}
          setDateFiledFilter={setDateFiledFilter}
          residencyFilter={residencyFilter}
          setResidencyFilter={setResidencyFilter}
          onViewDetails={handleViewDetails}
          onScheduleHearing={handleScheduleHearing}
          onAddNotes={handleAddNotes}
          onDelete={handleDeleteClick}
          pagination={{
            currentPage: page,
            lastPage,
            perPage,
            total,
          }}
          onPageChange={(newPage, newPerPage = perPage) => {
            setPage(newPage);
            if (newPerPage !== perPage) {
              setPerPage(newPerPage);
            }
          }}
        />
      </div>

      {/* Modals */}
      {showForm && (
        <DisputeFormModal
          initialData={selectedDispute}
          onSubmit={handleFormSubmit}
          onClose={() => setShowForm(false)}
        />
      )}

      {showDetails && selectedDispute && (
        <DisputeDetailsModal
          dispute={selectedDispute}
          onClose={() => setShowDetails(false)}
          onUpdate={() => {
            fetchDisputes();
            fetchStatistics();
          }}
        />
      )}

      {showSchedule && selectedDispute && (
        <ScheduleHearingModal
          dispute={selectedDispute}
          onSubmit={handleScheduleSubmit}
          onClose={() => setShowSchedule(false)}
        />
      )}

      {showNotes && selectedDispute && (
        <DisputeNotesModal
          dispute={selectedDispute}
          onSubmit={handleNotesSubmit}
          onClose={() => setShowNotes(false)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        open={confirmOpen}
        title='Confirm Deletion'
        message={`Are you sure you want to delete the blotter record with Case No. ${
          confirmTarget?.case_number || ''
        }? This action cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        isLoading={confirmLoading}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

export default DisputesPage;
